<template>
  <div class="bulk-order-gift-cards-index">
    <div class="container mb-16">
      <PageHeadlineForBulkOrder
        class="gift-card-header"
        title="ギフトカードリスト"
        :step="1"
        :button-type="ButtonType.PRIMARY"
        button-label="ギフトカード追加"
        :button-icon-path="WhitePlusCircleImage"
        button-icon-alt="ギフトカードの追加"
        :error-messages="errorMessages"
        :box-list-error-messages="boxListErrorMessages"
        @clicked="onClickAddGiftCardButton"
      />
    </div>
    <div v-if="!hasGiftCards" class="container">
      <div class="gift-card-patterns-empty">
        <img
          :src="LotsOfGiftsImage"
          alt="ギフトカードを追加してみましょう"
          class="gift-card-patterns-empty__image"
        />
        <p class="gift-card-patterns-empty__title">
          ギフトカードを追加してみましょう
        </p>
      </div>
    </div>
    <div v-else class="gift-card-patterns">
      <div
        v-for="(giftCardPattern, index) in giftCardPatterns"
        :key="index"
        class="container"
      >
        <GiftCardDetail
          class="gift-card-patterns__content"
          :token="giftCardPattern.token"
          :title="giftCardPattern.title"
          :publish-amount="giftCardPattern.publishAmount"
          :total-price="giftCardPattern.totalPrice"
          :is-total-price-including-tax="
            giftCardPattern.isTotalPriceIncludingTax
          "
          :gift-card-image-url="giftCardPattern.giftCardImageUrl"
          :sender-name="giftCardPattern.senderName"
          :message="giftCardPattern.message"
          :item-sku-contents="giftCardPattern.itemSkuContents"
          @click-preview="onClickPreviewGiftCard"
          @click-copy="onClickCopyGiftCard"
          @click-edit="onClickEditGiftCard"
          @click-delete="onClickDeleteGiftCard"
        />
      </div>
      <div v-if="targetGiftCard">
        <form
          class="gift-card-patterns__form"
          name="deleteGiftCardForm"
          method="post"
          :action="`/bulk_order/gift_cards/${targetGiftCard.token}`"
        >
          <input type="hidden" name="authenticity_token" :value="csrfToken" />
          <input type="hidden" name="_method" value="DELETE" />
          <input type="submit" />
        </form>
        <TextModal
          v-show="showDeleteGiftCardModal"
          :title-text="
            `ギフトカード\n「${targetGiftCard.title}」\nを削除しますか？`
          "
          primary-button-text="削除する"
          :primary-button-disabled="submitGiftCardDeleteButton"
          cancel-button-text="キャンセル"
          @clicked-modal-cover="closeDeleteGiftCardModal"
          @clicked-primary="onClickDeleteGiftCardModal"
          @clicked-cancel="closeDeleteGiftCardModal"
        />
      </div>
    </div>
    <div class="container mt-16">
      <div class="gift-card-patterns-bottom">
        <div class="gift-card-patterns-bottom__summary">
          <div class="summary-price-label">
            <span class="summary-price-label__title">決済合計</span>
            <span
              v-if="isSummaryPriceIncludingTax"
              class="summary-price-label__tax"
            >
              （税込）
            </span>
          </div>
          <span class="summary-price-content">
            ¥{{ summaryPrice.toLocaleString() }}
          </span>
        </div>
        <div
          v-if="hasBottomErrorMessage"
          class="gift-card-patterns-bottom__error"
        >
          <p v-for="(bottomError, index) in bottomErrors" :key="index">
            {{ bottomError }}
          </p>
        </div>
        <ButtonPrimary
          text="この内容で申し込む"
          :disabled="hasGiftCardListError || submitGiftCardListButton || null"
          @click-primary-button="onClickSubmitGiftCardListButton"
        />
      </div>
    </div>
    <div class="gift-card-patterns-footer mt-16">
      <TextLinkToTop class="my-16" />
    </div>
    <BulkPersonalOrderAgreementModal
      v-if="showBulkOrderAgreementModal"
      :bulk-personal-order-session-id="bulkPersonalOrderSessionId"
      @click-create-gift-card="onClickCreateGiftCard"
    />
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import PageHeadlineForBulkOrder, {
  ButtonType
} from '../../../organism/bulk_personal_orders/PageHeadlineForBulkOrder.vue'
import WhitePlusCircleImage from '../../../../../../images/icons/white-plus-circle.svg'
import LotsOfGiftsImage from '../../../../../../images/bulk_personal_orders/lots_of_gifts.png'
import GiftCardDetail from '../../../organism/bulk_personal_orders/gift_cards/GiftCardDetail.vue'
import TextLinkToTop from '../../../organism/bulk_personal_orders/TextLinkToTop.vue'
import TextModal from '../../../molecules/modals/text-modal.vue'
import ButtonPrimary from '../../../molecules/buttons/ButtonPrimary.vue'
import BulkPersonalOrderAgreementModal from '../../../organism/bulk_personal_orders/BulkPersonalOrderAgreementModal.vue'
import { useBulkPersonalOrderSessionStore } from '../../../../store/bulkPersonalOrderSessionStore'
import { useBulkPersonalOrderSessionGiftCardStore } from '../../../../store/bulkPersonalOrderSessionGiftCard'

const MIN_TOTAL_PUBLISH_AMOUNT = 11
const showDeleteGiftCardModal = ref(false)
const submitGiftCardDeleteButton = ref(false)
const submitGiftCardListButton = ref(false)
const targetGiftCard = ref(null)
const showBulkOrderAgreementModal = ref(false)
const bottomErrors = ref([])

const csrfToken = document
  .querySelector('meta[name="csrf-token"]')
  .getAttribute('content')

const props = defineProps({
  bulkPersonalOrderSessionId: {
    type: Number,
    required: true
  },
  giftCardPatterns: {
    type: Array,
    required: true
  },
  summaryPrice: {
    type: Number,
    required: true
  },
  firstSession: {
    type: Boolean,
    required: true
  },
  boxListErrorMessages: {
    type: Array,
    required: false,
    default: () => []
  },
  errorMessages: {
    type: Array,
    required: false,
    default: () => []
  },
  bottomErrorMessages: {
    type: Array,
    required: false,
    default: () => []
  }
})

onMounted(() => {
  // 初回のセッションの場合は、まとめてオーダーの同意のモーダルを表示させる
  if (props.firstSession) {
    // 初回セッションの場合は、まとめてオーダー関連のローカルストレージを初期化
    $reset()
    showBulkOrderAgreementModal.value = true
  } else {
    // 初回以降のセッションの場合は、ローカルストレージの状態で表示制御を行う
    showBulkOrderAgreementModal.value = !isAgreementBulkOrder
  }

  buildBottomErrorMessages()

  dataLayer.push({
    event: 'view_bulk_gift_cards',
    bulk_personal_order_session_id: props.bulkPersonalOrderSessionId,
    pattern_count: props.giftCardPatterns.length
  })
})

const sessionStore = useBulkPersonalOrderSessionStore()
const { isAgreementBulkOrder, $reset } = sessionStore
const giftCardStore = useBulkPersonalOrderSessionGiftCardStore()
const { setGiftCardForm, deleteGiftCard } = giftCardStore

const hasGiftCards = props.giftCardPatterns.length > 0

const totalGiftCardPublishAmount = props.giftCardPatterns.reduce(
  (total, giftCardPattern) => total + giftCardPattern.publishAmount,
  0
)

const isSummaryPriceIncludingTax = props.giftCardPatterns.some(
  giftCardPattern => giftCardPattern.isTotalPriceIncludingTax
)

const missingPublishAmount =
  MIN_TOTAL_PUBLISH_AMOUNT - totalGiftCardPublishAmount

const validTotalGiftCardPublishAmount =
  totalGiftCardPublishAmount >= MIN_TOTAL_PUBLISH_AMOUNT

const hasBottomErrorMessage = computed(() => bottomErrors.value.length > 0)

const hasGiftCardListError = computed(() => hasBottomErrorMessage.value)

const buildBottomErrorMessages = () => {
  // バックエンドからのエラーがある場合はそちらを優先で表示する
  if (props.bottomErrorMessages.length > 0) {
    bottomErrors.value = props.bottomErrorMessages
    return
  }

  if (!validTotalGiftCardPublishAmount) {
    bottomErrors.value.push(
      `ギフトカード発行数があと${missingPublishAmount}件必要です。申し込みへ進むにはギフトカードの合計発行数が${MIN_TOTAL_PUBLISH_AMOUNT}件以上になるように追加してください。`
    )
  }
}

const onClickAddGiftCardButton = () => {
  dataLayer.push({
    event: 'click_bulk_gift_cards__add',
    bulk_personal_order_session_id: props.bulkPersonalOrderSessionId
  })
  // ギフトカード追加ボタンを押した場合は、編集中のデータは削除する
  deleteGiftCard('new')
  window.location.href = `/bulk_order/gift_cards/new`
}

const onClickPreviewGiftCard = () => {
  dataLayer.push({
    event: 'click_bulk_gift_cards__preview',
    bulk_personal_order_session_id: props.bulkPersonalOrderSessionId
  })
}

const onClickCopyGiftCard = token => {
  dataLayer.push({
    event: 'click_bulk_gift_cards__duplicate',
    bulk_personal_order_session_id: props.bulkPersonalOrderSessionId
  })

  const targetGiftCard = props.giftCardPatterns.find(
    giftCardPattern => giftCardPattern.token == token
  )

  // 一旦削除
  deleteGiftCard('new')

  // コピー
  setGiftCardForm({
    giftCardSessionToken: 'new',
    title: `パターン${props.giftCardPatterns.length + 1}`,
    isCardDesign: targetGiftCard.isCardDesign,
    cardDesignId: targetGiftCard.isCardDesign
      ? targetGiftCard.cardDesignId
      : '',
    cardDesignImageUrl: targetGiftCard.isCardDesign
      ? targetGiftCard.cardDesignImageUrl
      : '',
    personalCardDesignId: '',
    personalCardDesignFileName: !targetGiftCard.isCardDesign
      ? targetGiftCard.personalCardDesignFileName
      : '',
    personalCardDesignDataUri: !targetGiftCard.isCardDesign
      ? targetGiftCard.personalCardDesignDataUri
      : '',
    isAgreementForPersonalCardDesign: !targetGiftCard.isCardDesign,
    message: targetGiftCard.message,
    senderName: targetGiftCard.senderName,
    publishAmount: targetGiftCard.publishAmount,
    itemSkuContentAttributes: targetGiftCard.itemSkuContents
  })

  window.location.href = `/bulk_order/gift_cards/new`
}

const onClickEditGiftCard = token => {
  dataLayer.push({
    event: 'click_bulk_gift_cards__edit',
    bulk_personal_order_session_id: props.bulkPersonalOrderSessionId
  })
  //編集中のデータは削除する
  deleteGiftCard(token)
  window.location.href = `/bulk_order/gift_cards/${token}/edit`
}

const onClickDeleteGiftCard = token => {
  showDeleteGiftCardModal.value = true
  targetGiftCard.value = props.giftCardPatterns.find(
    giftCardPattern => giftCardPattern.token == token
  )
}

const closeDeleteGiftCardModal = () => {
  showDeleteGiftCardModal.value = false
  targetGiftCard.value = null
}

const onClickDeleteGiftCardModal = () => {
  dataLayer.push({
    event: 'click_bulk_gift_cards__delete',
    bulk_personal_order_session_id: props.bulkPersonalOrderSessionId
  })
  submitGiftCardDeleteButton.value = true
  document.deleteGiftCardForm.submit()
}

const onClickCreateGiftCard = () => {
  dataLayer.push({
    event: 'click_bulk_precautions_modal__proceed',
    bulk_personal_order_session_id: props.bulkPersonalOrderSessionId
  })
  showBulkOrderAgreementModal.value = false
}

const onClickSubmitGiftCardListButton = () => {
  dataLayer.push({
    event: 'click_bulk_gift_cards__proceed',
    bulk_personal_order_session_id: props.bulkPersonalOrderSessionId
  })
  submitGiftCardListButton.value = true
  document.location.href = `/bulk_order/orderer_detail/new`
}
</script>

<style lang="scss" scoped>
.bulk-order-gift-cards-index {
  .container {
    display: flex;
    justify-content: center;
    background-color: $white;
  }

  .gift-card-container {
    display: flex;
    justify-content: center;
  }

  .gift-card-header {
    max-width: $small-contents-width;
    width: 100%;
  }

  .gift-card-patterns-empty {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    max-width: $small-contents-width;
    width: 100%;
    padding: 32px 16px;
    gap: 16px;
    box-sizing: border-box;

    &__image {
      width: 200px;
      object-fit: contain;
    }
    &__title {
      font-size: 1.6rem;
      line-height: 1.6;
      font-weight: bold;
      color: $black;
    }
  }

  .gift-card-patterns {
    & > * {
      margin-bottom: 16px;
    }

    &__content {
      max-width: $small-contents-width;
      width: 100%;
    }

    &__form {
      display: none;
    }
  }

  .gift-card-patterns-bottom {
    max-width: $small-contents-width;
    width: 100%;
    padding: 24px 16px;
    box-sizing: border-box;

    & > * {
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    &__summary {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .summary-price-label {
        display: flex;
        justify-content: center;
        align-items: center;

        &__title {
          font-size: 1.6rem;
          line-height: 1.6;
          font-weight: bold;
          color: $black;
        }
        &__tax {
          font-size: 1.4rem;
          line-height: 1.6;
          color: $black;
        }
      }

      .summary-price-content {
        display: inline-block;
        font-size: 1.8rem;
        line-height: 1.6;
        font-weight: bold;
        color: $black;
      }
    }

    &__error {
      font-size: 1.2rem;
      line-height: 1.6;
      color: $danger;
      text-align: left;
    }
  }

  .gift-card-patterns-footer {
    display: flex;
    justify-content: center;
  }
}
</style>
